import React, {useCallback, useEffect, useRef, useState} from "react";
import {useEventListener} from "usehooks-ts";
import Seat from "./Seat/Seat";
import {
    drawSelectionArea,
    getSVGPoint,
    intersectDim,
    isStartDrawing,
} from "./utils";
import classes from "./Schema.module.css";
import {useTranslation} from "react-i18next";
import {TText} from "../../pages/sales/sales.slice";
import FanZone from "./FanZone/FanZone";
import _debounce from "lodash/debounce"

let offset = {
    x: 0,
    y: 0,
};
let rectStartX = 0;
let rectStartY = 0;
let startX = 0;
let startY = 0;

let scale = 1,
    maxScale = 3,
    minScale = 0.1,
    isDragging = false

interface IProps {
    data: {
        hall_parts_data?: any;
        hall_parts?: any[]
        data: {
            [key: string]: {
                id: number;
                zone: number | null;
                selected?: boolean;
                ticket?: number;
                status?: "free" | "reserved" | "sold" | "disabled" | "refunded";
                rect: {
                    x: string;
                    y: string;
                };
            };
        };
    };
    text?: TText;
    zones: any[];
    viewBox?: string;
    type: "price" | "sales";
    preferredLanguage?: "ro" | "en" | "ru";
    showOnly?: "reserved" | "free" | "sold";
    widgetMode?: boolean;
    blockSelection?: boolean;
    showHints?: boolean;
    styles?: React.CSSProperties;
    onScaleChange?: (scale: number) => void;
    onSeatClick?: (
        id: number,
        key: string,
        zone?: number | null,
        status?: "free" | "reserved" | "sold" | "disabled" | "refunded",
        ticket?: number,
        isShortCut?: boolean
    ) => void;
    onTextClick?: (e: React.MouseEvent<SVGTextElement>) => void;
    onPolylineClick?: (e: React.MouseEvent<SVGTextElement>) => void;
    onRectClick?: (e: React.MouseEvent<SVGTextElement>) => void;
    onCircleClick?: (e: React.MouseEvent<SVGTextElement>) => void;
    onFanZoneClick?: (e: React.MouseEvent<SVGTextElement>, fanId?: number) => void;
    onSelectionArea?: (tmpSelection: any[], collectedSeats: any[]) => void;
    onClick?: (e: React.MouseEvent<SVGSVGElement>) => void;
    onRightClick?: (e: React.MouseEvent) => void;
    clientScreen?: boolean //PROPERTY FOR SECOND SCREEN IN PHYSICAL CASH-BOXES (for hiding something)
    isLoadingSchema?: boolean,
    schemeId?: number
}

const Schema = (props: IProps) => {
    const {
        data,
        zones,
        blockSelection,
        styles,
        onSeatClick,
        onTextClick,
        onPolylineClick,
        onRectClick,
        onCircleClick,
        onSelectionArea,
        onClick,
        onRightClick,
        onFanZoneClick,
        showHints,
        showOnly,
        widgetMode,
        type,
        preferredLanguage,
        text,
        clientScreen,
        isLoadingSchema,
        schemeId
    } = props;
    const {
        i18n: {language},
    } = useTranslation();
    const containerRef = useRef<any>(null);
    const tmpSelection = useRef<string[]>([]);
    const viewportRef = useRef<SVGSVGElement | null>(null);
    const selectionAreaRef = useRef<SVGRectElement | null>(null);
    const collectedSeats = useRef<any[]>([]);
    const [initialPosition, setInitialPosition] = useState<{ matrix?: SVGTransform, scale?: number }>({})
    const moveSvg = useCallback((x: number, y: number, move?: boolean) => {
        const viewport = viewportRef.current;
        const container = containerRef.current;

        // Guard against null refs or invalid move state
        if (!viewport || !container || !(isDragging || move)) return;

        // Use null coalescing to ensure we have a string for parseFloat
        const startX = parseFloat(viewport.dataset.startMouseX ?? "0");
        const startY = parseFloat(viewport.dataset.startMouseY ?? "0");

        const deltaX = x - startX;
        const deltaY = y - startY;

        try {
            const baseTransform = viewport.transform.baseVal;
            // Consolidate current transforms or fallback to identity matrix
            const currentMatrix = baseTransform.consolidate()?.matrix
                || container.createSVGMatrix();

            const newMatrix = currentMatrix.translate(
                deltaX / scale,
                deltaY / scale
            );

            const transform = container.createSVGTransformFromMatrix(newMatrix);


            baseTransform.initialize(transform);

            // Update coordinates (simplified logic: deltaX + startX is just x)
            viewport.dataset.startMouseX = x.toString();
            viewport.dataset.startMouseY = y.toString();

        } catch (error) {
            console.error("SVG Transform failed:", error);
        }
    }, []);

    const setInitialScale = useCallback(() => {
        const viewport = viewportRef.current;
        const container = containerRef.current;
        if (viewport && container) {
            const sBox = container.getBoundingClientRect();
            // We use the current viewport rect to calculate the scale
            const cBox = viewport.getBoundingClientRect();

            // 1. Define available space (subtracting bottom bar)
            const targetWindow = containerRef.current?.ownerDocument?.defaultView || window;
            const containerWidth = clientScreen ? targetWindow.innerWidth - 400 : sBox.width;
            const containerHeight = clientScreen ? targetWindow.innerHeight - 200 : sBox.height - 50;

            // 2. Get the natural size of the SVG content
            // Note: BoundingClientRect changes after scaling.
            // If this runs twice, cBox.width might already be scaled.
            const svgWidth = cBox.width;
            const svgHeight = cBox.height;

            const scaleX = containerWidth / svgWidth;
            const scaleY = containerHeight / svgHeight;

            let newScale = Math.min(scaleX, scaleY);

            // Don't upscale past 100%
            if (newScale > 1) {
                newScale = 1;
            }

            // 3. Update the external 'scale' variable (ensure 'scale' is a ref or state)
            // If 'scale' is a ref: setScale(newScale) or scaleRef.current = newScale
            scale = newScale;
            const matrix = `matrix(${newScale}, 0, 0, ${newScale}, 0, 0)`
            viewport.setAttribute(
                "transform",
                matrix
            );

        }
    }, []);

    const centerAndScaleViewport = useCallback(() => {
        const container = containerRef.current;
        const viewport = viewportRef.current;
        if (!container || !viewport) return;


        const sBox = container.getBoundingClientRect();


        const bbox = viewport.getBBox();
        if (bbox.width === 0 || bbox.height === 0) return; // Prevent Infinity errors


        const targetWindow = containerRef.current?.ownerDocument?.defaultView || window;


        const containerWidth = clientScreen ? targetWindow.innerWidth - 500 : sBox.width;
        const containerHeight = clientScreen ? targetWindow.innerHeight - 140 : sBox.height - 50;


        const scaleX = containerWidth / bbox.width;
        const scaleY = containerHeight / bbox.height;

        let MathScale = Math.min(scaleX, scaleY);
        if (MathScale > 1) {
            MathScale = 1; // Don't upscale past 100%
        }

        scale = MathScale;

        const scaledWidth = bbox.width * scale;
        const scaledHeight = bbox.height * scale;

        // Center offsets (accounting for the BBox's original X/Y starting points)
        const offsetX = (containerWidth - scaledWidth) / 2 - (bbox.x * scale);
        const offsetY = ((containerHeight - scaledHeight) / 2 - (bbox.y * scale)) + 30;

        const newMatrix = container.createSVGMatrix()
            .translate(offsetX, offsetY)
            .scale(scale);

        const transform = container.createSVGTransformFromMatrix(newMatrix);
        viewport.transform.baseVal.initialize(transform);

        setInitialPosition({
            scale: scale,
            matrix: transform
        });

        viewport.dataset.startMouseX = "0";
        viewport.dataset.startMouseY = "0";

    }, [clientScreen]);

    useEffect(() => {
        const container = containerRef.current;
        const viewport = viewportRef.current;

        if (isLoadingSchema || !schemeId || !container || !viewport) return;

        // Use requestAnimationFrame instead of setTimeout(0)
        // This ensures the browser has painted before we measure rects
        const rafId = requestAnimationFrame(() => {
            centerAndScaleViewport();
            window.scrollTo(0, 1);
        });

        return () => cancelAnimationFrame(rafId);
    }, [isLoadingSchema, schemeId, setInitialScale, moveSvg]);

    //HANDLERS
    function mouseMove(e: PointerEvent) {
        e.preventDefault();

        if (!onSelectionArea) {
            return;
        }

        //create selection area
        if (containerRef && e.buttons === 1 && !e.ctrlKey && !blockSelection) {
            const isStart = isStartDrawing(e, containerRef, startX, startY);
            //draw selection criteria only when size is more than 6px
            if (!isStart) return;

            drawSelectionArea(e, selectionAreaRef, containerRef, startX, startY);

            if (selectionAreaRef && selectionAreaRef.current) {
                //temporary svg rect to determine intersection area
                const rect = intersectDim(
                    e,
                    containerRef,
                    offset,
                    rectStartX,
                    rectStartY
                );

                const intersections = containerRef.current.getIntersectionList(
                    rect,
                    null
                );
                const intersectionsArray = Array.from<SVGSVGElement>(intersections);
                tmpSelection.current = intersectionsArray.map(
                    (element: SVGGraphicsElement) => {
                        const tag = element.tagName.toLowerCase();
                        const id = element.getAttribute("id");

                        if (!id) return "";

                        if (tag === "text") return `text:${id}`;
                        if (tag === "polyline") return `polyline:${id}`;
                        if (tag === "rect") {
                            if (element.getAttribute("data-type") === "fan-zone") {
                                return `fan-zone:${id}`;
                            } else {
                                return `rect:${id}`;
                            }
                        }
                        if (
                            tag === "circle" &&
                            element.getAttribute("type") === "customCircle"
                        )
                            return `circle:${id}`;

                        return id;
                    }
                );

                collectedSeats.current = intersectionsArray.map(
                    (circle: SVGSVGElement) => {
                        if (circle.getAttribute("type") !== "customCircle") {
                            return data.data[circle.getAttribute("id") || ""];
                        }
                    }
                );
            }
        }
    }

    function mouseClickDown(e: PointerEvent) {
        if (onSelectionArea) {
            const parentOffset = containerRef.current.getBoundingClientRect();
            const point = getSVGPoint(e.clientX, e.clientY, containerRef.current);
            offset = {
                x: e.clientX,
                y: e.clientY,
            };
            startX = point.x;
            startY = point.y;
            rectStartY = offset.y - parentOffset.top;
            rectStartX = offset.x - parentOffset.left;
        }
    }

    function mouseClickUp(e: PointerEvent) {
        //remove selection area
        const rect = selectionAreaRef.current;
        if (rect) {
            rect.setAttribute("width", "0");
            rect.setAttribute("height", "0");
        }

        //set last user's mouse offset
        offset = {
            x: e.clientX,
            y: e.clientY,
        };

        if (!tmpSelection.current.length && !collectedSeats.current.length) return;
        onSelectionArea &&
        onSelectionArea(tmpSelection.current, collectedSeats.current);
        //clear collected seats
        collectedSeats.current = [];
        tmpSelection.current = [];
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.key === 'r') {
            resetToInitialPosition();
        }
    }

    function zoomSvg(delta: number, x: number, y: number, isTouch?: boolean) {
        const svg = containerRef.current
        const container = viewportRef.current
        if (svg && container) {
            try {
                let scaleStep =
                    delta < 0 ? (isTouch ? 1.05 : 1.25) : isTouch ? 0.3 : 0.8

                if (
                    scale * scaleStep > maxScale ||
                    scale * scaleStep < minScale
                ) {
                    scaleStep = 1
                } else {
                    scale *= scaleStep
                }

                let box = svg.getBoundingClientRect()
                let point = svg.createSVGPoint()
                point.x = x - box.left
                point.y = y - box.top

                let currentZoomMatrix = container.getCTM()

                //Check for zoom matrix
                if (!currentZoomMatrix) return;

                point = point.matrixTransform(currentZoomMatrix.inverse())

                let matrix = svg
                    .createSVGMatrix()
                    .translate(point.x, point.y)
                    .scale(scaleStep)
                    .translate(-point.x, -point.y)

                let newZoomMatrix = currentZoomMatrix.multiply(matrix)

                container.transform.baseVal.initialize(
                    svg.createSVGTransformFromMatrix(newZoomMatrix)
                )
            } catch (e) {
                console.log(e)
            }
        }
    }

    function resetToInitialPosition() {
        const viewport = viewportRef.current;
        if (viewport && initialPosition.matrix && initialPosition.scale) {
            // 1. Reset the local scale variable used for zooming
            scale = initialPosition.scale;

            // 2. Re-apply the initial matrix to the SVG viewport
            viewport.transform.baseVal.initialize(initialPosition.matrix);

            // 3. Reset dataset coordinates to prevent jumping on the next mouse movement
            viewport.dataset.startMouseX = "0";
            viewport.dataset.startMouseY = "0";
        }
    }

    const handleWheel = (e: WheelEvent) => {
        e.stopPropagation()
        e.preventDefault()

        if(clientScreen) return;

        const delta = e.deltaY
        zoomSvg(delta, e.clientX, e.clientY)
    }

    const handleMouseUp = () => {
        isDragging = false
    }
    const handleMouseDown = (e: React.MouseEvent<SVGGElement>) => {


        // Check if the ref currently points to an element
        const viewport = viewportRef.current;

        if (viewport) {
            viewport.dataset.startMouseX = e.clientX.toString();
            viewport.dataset.startMouseY = e.clientY.toString();
        }

        if((type === 'price' && !e.ctrlKey) || clientScreen) {
            isDragging = false
            return;
        }

        isDragging = true;
    }
    const handleMouseMove = _debounce((e: React.MouseEvent<SVGGElement>) => {
        moveSvg(e.clientX, e.clientY)
    }, 0.16)
    const handleResize = _debounce(() => {
        if (clientScreen) {
            centerAndScaleViewport();
        }
    }, 200);
    //LISTENERS
    useEffect(() => {
        // Get the specific window where the SVG is rendered
        const targetWindow = containerRef.current?.ownerDocument?.defaultView || window;

        targetWindow.addEventListener("resize", handleResize);

        return () => {
            targetWindow.removeEventListener("resize", handleResize);
        };
    }, [clientScreen, handleResize]);
    useEventListener("wheel", handleWheel, containerRef);
    useEventListener("pointermove", mouseMove, containerRef);
    useEventListener("pointerdown", mouseClickDown, containerRef);
    useEventListener("pointerup", mouseClickUp, containerRef);
    useEventListener("keydown", onKeyDown);

    return (
        <svg
            id={"svg"}
            ref={containerRef}
            width={"100%"}
            height={"100%"}
            // viewBox={viewBox}
            preserveAspectRatio="xMinYMin meet"
            onClick={onClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onContextMenu={(e) => {
                e.preventDefault();
                if (typeof onRightClick === "function") {
                    onRightClick(e);
                }
            }}
            style={{
                background: styles?.background,
            }}
            // style={styles}
        >
            <g id={"container"} ref={viewportRef} transform={`matrix(1 0 0 1 0 0)`}>
                {text &&
                    Array.isArray(text) &&
                    text.map((textItem, index) => {
                        const {properties, styles, rect} = textItem;
                        const id = index.toString();

                        switch (properties.type) {
                            case "text":
                                return (
                                    <text
                                        id={index.toString()}
                                        onClick={onTextClick}
                                        key={index}
                                        textAnchor="middle"
                                        alignmentBaseline="central"
                                        fontSize={styles.fontSize}
                                        fontWeight={500}
                                        x={rect.x}
                                        y={rect.y}
                                        fill={styles.fill ? styles.fill : "#949494"}
                                        transform={styles.transform}
                                        writingMode={styles.writingMode}
                                        style={{
                                            userSelect: "none",
                                            transformOrigin: styles.transformOrigin,
                                        }}
                                    >
                                        {typeof properties.text === "object"
                                            ? properties.text[
                                                preferredLanguage
                                                    ? preferredLanguage
                                                    : (language as keyof typeof properties.text)
                                                ]
                                            : properties.text}
                                    </text>
                                );

                            case "rect":
                                return (
                                    <rect
                                        id={id}
                                        onClick={(e) => onRectClick!(e as any)}
                                        key={index}
                                        x={rect.x}
                                        y={rect.y}
                                        fill={styles.fill}
                                        width={styles.width}
                                        height={styles.height}
                                        rx={styles.rx}
                                        ry={styles.ry}
                                    />
                                );

                            case "circle":
                                return (
                                    <circle
                                        id={id}
                                        key={index}
                                        type="customCircle"
                                        onClick={(e) =>
                                            onCircleClick ? onCircleClick(e as any) : null
                                        }
                                        cx={rect.x}
                                        cy={rect.y}
                                        r={rect.r}
                                        fill={styles.fill}
                                        stroke={styles.stroke}
                                        strokeWidth={styles.strokeWidth}
                                    />
                                );

                            case "polyline":
                                return (
                                    <polyline
                                        onClick={(e) => onPolylineClick!(e as any)}
                                        id={id}
                                        key={index}
                                        points={rect.points}
                                        stroke={styles.stroke}
                                        strokeWidth={styles.strokeWidth}
                                        fill={styles.fill}
                                    />
                                );

                            default:
                                return <></>;
                        }
                    })}
                {data &&
                    data.data &&
                    Object.keys(data.data).map((key, i) => {
                        const {rect, id, zone, selected, status, ticket} =
                            data.data[key as keyof typeof data.data];
                        const zoneIndex = zones.map((z) => z.id).indexOf(zone || -1);
                        const fill =
                            zoneIndex !== -1 ? zones[zoneIndex].properties.color : "#a6a6a6";
                        if (!rect) {
                            return <></>;
                        }
                        return (
                            <MemoizedSeat
                                key={key}
                                x={String(Number(rect.x) + 20)}
                                y={rect.y}
                                id={id}
                                seatKey={key}
                                zone={zone}
                                ticket={ticket}
                                fill={fill}
                                status={status}
                                price={zones[zoneIndex]?.price}
                                isSelected={Boolean(selected)}
                                onClick={onSeatClick}
                                data-testid={`grid-cell-${i}`}
                                showOnly={showOnly}
                                widgetMode={widgetMode}
                                type={type}
                            />
                        );
                    })}

                {data?.hall_parts && Array.isArray(data.hall_parts) && (
                    <FanZone
                        preferredLanguage={preferredLanguage}
                        onFanZoneClick={onFanZoneClick}
                        fanZones={data.hall_parts}
                        zones={zones}
                        clientScreen={clientScreen}
                    />
                )}

                {data?.hall_parts_data && Array.isArray(data.hall_parts_data) && (
                    <FanZone
                        preferredLanguage={preferredLanguage}
                        onFanZoneClick={onFanZoneClick}
                        fanZones={data.hall_parts_data}
                        zones={zones}
                    />
                )}
            </g>

            {showHints && (
                <g>
                    <rect
                        id={"seatTooltip"}
                        className={classes.rectTooltip}
                        width={0}
                        height={0}
                        fill={"#000"}
                    />
                    <text
                        id={"zone"}
                        className={classes.textTooltip}
                        style={{fontSize: 10}}
                        fill={"#fff"}
                    />
                    <text
                        id={"seat"}
                        className={classes.textTooltip}
                        style={{fontSize: 10}}
                        fill={"#fff"}
                    />
                    <text
                        id={"price"}
                        className={classes.textTooltip}
                        style={{fontSize: 10}}
                        fill={"#fff"}
                    />
                </g>
            )}
            <rect
                ref={selectionAreaRef}
                id={"selectionArea"}
                width="0"
                height="0"
                style={{
                    fill: "rgba(47,255,255,0.1)",
                    stroke: "rgba(42,57,227,0.9)",
                    strokeWidth: 1,
                }}
            />
        </svg>
    );
};

export default Schema;

const MemoizedSeat = React.memo(Seat);
